/**
 * AAR replay timeline assembly (#180) — PURE. Turns the read-only replay
 * response (operator-action audit rows + anomaly lifecycle timestamps) into a
 * scrub-able timeline: clock bounds, period ticks, event offsets, and per-
 * anomaly Law-3 keyframes (urgent → acknowledged → cleared).
 *
 * Deliberately Cesium-free: the controller (aarReplay.ts) maps states to
 * entity styling; everything time-shaped lives here so it unit-tests without
 * WebGL. Replay time is a plain epoch-ms scalar — NEVER the shared
 * viewer.clock, which drives the LIVE aircraft SampledPositionProperty motion
 * (#173) and must keep ticking wall-clock underneath the replay.
 */
import type {
  AarReplayAnomaly,
  AarReplayEvent,
  AarReplayPeriod,
  AarReplayResponse,
  AarReplayTrackSegment,
} from '@tsr/shared-types';

/** Law-3 lifecycle state at a point in replay time. 'pre' = not yet raised. */
export type AarLifecycleState = 'pre' | 'urgent' | 'acknowledged' | 'cleared';

export interface AarAnomalyKeyframe {
  tMs: number;
  state: Exclude<AarLifecycleState, 'pre'>;
}

export interface AarAnomalyTrack {
  anomaly: AarReplayAnomaly;
  /** Sorted ascending; state at time t = the last keyframe with tMs <= t. */
  keyframes: AarAnomalyKeyframe[];
}

export interface AarTimelineEvent {
  event: AarReplayEvent;
  tMs: number;
}

export interface AarPeriodTick extends AarReplayPeriod {
  startMs: number;
  endMs: number;
}

/** #485 — a deactivation→reactivation gap on the scrub clock (epoch ms). */
export interface AarDeactivationGapMs {
  startMs: number;
  endMs: number;
}

export interface AarReplayTimeline {
  startMs: number;
  endMs: number;
  closed: boolean;
  periods: AarPeriodTick[];
  /** Sorted ascending by time. */
  events: AarTimelineEvent[];
  tracks: AarAnomalyTrack[];
  /** #485 — spans where the incident was deactivated then reactivated. The overlay shades
   *  these "was deactivated" so a reopened incident's clock is not read as continuously active.
   *  Empty for an incident never reactivated; invalid/degenerate spans are dropped. */
  gaps: AarDeactivationGapMs[];
}

const ms = (iso: string): number => new Date(iso).getTime();

/**
 * Build one anomaly's keyframe track from its recorded lifecycle timestamps.
 * Unrecorded transitions simply produce no keyframe (a hotspot with no
 * cleared_at stays in its last recorded state — the honest rendering).
 * Out-of-order records (e.g. an ack stamped after the resolve) are sorted by
 * time so stateAt() stays a simple last-keyframe-wins scan.
 */
export function buildAnomalyKeyframes(a: AarReplayAnomaly): AarAnomalyKeyframe[] {
  const frames: AarAnomalyKeyframe[] = [];
  const raised = ms(a.raised_at);
  if (Number.isFinite(raised)) frames.push({ tMs: raised, state: 'urgent' });
  if (a.acknowledged_at) {
    const t = ms(a.acknowledged_at);
    if (Number.isFinite(t)) frames.push({ tMs: t, state: 'acknowledged' });
  }
  if (a.ended_at) {
    const t = ms(a.ended_at);
    if (Number.isFinite(t)) frames.push({ tMs: t, state: 'cleared' });
  }
  frames.sort((x, y) => x.tMs - y.tMs);
  return frames;
}

/** Lifecycle state of a track at replay time t (last keyframe wins). */
export function anomalyStateAt(track: AarAnomalyTrack, tMs: number): AarLifecycleState {
  let state: AarLifecycleState = 'pre';
  for (const kf of track.keyframes) {
    if (kf.tMs > tMs) break;
    state = kf.state;
  }
  return state;
}

/** Number of events that have occurred at or before t (events are sorted). */
export function eventCountAt(events: AarTimelineEvent[], tMs: number): number {
  let n = 0;
  for (const e of events) {
    if (e.tMs > tMs) break;
    n += 1;
  }
  return n;
}

/** The most recent event at or before t, or null before the first event. */
export function latestEventAt(events: AarTimelineEvent[], tMs: number): AarTimelineEvent | null {
  const n = eventCountAt(events, tMs);
  return n === 0 ? null : events[n - 1]!;
}

/** Clamp a scrub position into the replay window. */
export function clampReplayTime(timeline: AarReplayTimeline, tMs: number): number {
  if (!Number.isFinite(tMs)) return timeline.startMs;
  return Math.min(Math.max(tMs, timeline.startMs), timeline.endMs);
}

// ── Track motion (#238) ──────────────────────────────────────────────────────

/** One fix parsed onto the epoch-ms scrub clock. */
export interface AarTrackFixMs {
  tMs: number;
  lon: number;
  lat: number;
  altM: number | null;
}

export interface AarTrackTimeline {
  segment: AarReplayTrackSegment;
  /** Valid fixes, ascending by time (invalid timestamps dropped). */
  fixes: AarTrackFixMs[];
  firstMs: number;
  lastMs: number;
}

/**
 * Marker hold past the LAST fix — one downsample bucket (5 min), so a track
 * whose tail sits in the 5-min-sampled tier doesn't vanish a frame after its
 * final sample. Before the first fix the marker is hidden (no back-projection:
 * we don't know where it was). Interpolation bridges gaps BETWEEN fixes.
 */
export const TRACK_HOLD_MS = 5 * 60_000;

/** Parse response track segments onto the scrub clock; segments with no valid
 *  fixes are dropped. `tracks` is optional-tolerant for older API responses. */
export function buildTrackTimelines(
  tracks: AarReplayTrackSegment[] | undefined,
): AarTrackTimeline[] {
  const out: AarTrackTimeline[] = [];
  for (const segment of tracks ?? []) {
    const fixes: AarTrackFixMs[] = segment.fixes
      .map((f) => ({ tMs: ms(f.t), lon: f.lon, lat: f.lat, altM: f.alt_m }))
      .filter((f) => Number.isFinite(f.tMs))
      .sort((a, b) => a.tMs - b.tMs);
    if (fixes.length === 0) continue;
    out.push({ segment, fixes, firstMs: fixes[0]!.tMs, lastMs: fixes[fixes.length - 1]!.tMs });
  }
  return out;
}

/** Whether a track's marker should show at scrub time t (Law 6: this only
 *  ever changes on an operator scrub). */
export function trackVisibleAt(track: AarTrackTimeline, tMs: number): boolean {
  return tMs >= track.firstMs && tMs <= track.lastMs + TRACK_HOLD_MS;
}

/**
 * Honest-chrome copy for the overlay footnote (#238). Distinguishes the three
 * truthful empty/non-empty states the 7-day retention spec produces:
 *  - tracks present → state the resolution contract;
 *  - none + the window ended before coverage began → the incident PREDATES
 *    position history (pre-0073 era or pruned past 7 d) — not an empty sky;
 *  - none but coverage overlaps → genuinely no tracked traffic nearby.
 */
export function trackCoverageNote(resp: AarReplayResponse): string {
  const tracks = resp.tracks ?? [];
  if (tracks.length > 0) {
    return (
      'Tracks span a 7-day history: full detail for the last 72 h, 5-minute samples beyond.'
    );
  }
  const coverageMs = resp.track_coverage_start ? ms(resp.track_coverage_start) : NaN;
  const windowEndMs = ms(resp.window.end);
  if (!Number.isFinite(coverageMs) || (Number.isFinite(windowEndMs) && windowEndMs < coverageMs)) {
    return 'This incident predates position-history coverage (7-day window) — aircraft/vessel tracks are unavailable.';
  }
  return 'No tracked aircraft or vessels were recorded near this incident during its window.';
}

/**
 * Assemble the full timeline. Always yields a valid, scrub-able window even
 * for an empty incident (zero events / anomalies / periods): the clock spans
 * opened_at → closed_at (or "now" when still open), and a degenerate window
 * (end <= start) is widened by one minute so the slider stays operable.
 */
export function buildReplayTimeline(resp: AarReplayResponse): AarReplayTimeline {
  const startMs = ms(resp.window.start);
  let endMs = ms(resp.window.end);
  if (!Number.isFinite(endMs) || endMs <= startMs) endMs = startMs + 60_000;

  const events: AarTimelineEvent[] = resp.events
    .map((event) => ({ event, tMs: ms(event.timestamp) }))
    .filter((e) => Number.isFinite(e.tMs))
    .sort((a, b) => a.tMs - b.tMs);

  // The close_incident audit row lands milliseconds after closed_at (the API
  // grants its query a small grace) — widen the clock so the slider's end can
  // reach every returned event. Ceil to a whole second: the scrubber steps in
  // 1s increments, so a millisecond-stamped end would otherwise sit just past
  // the last reachable slider position.
  const lastEvent = events[events.length - 1];
  if (lastEvent && lastEvent.tMs > endMs) endMs = Math.ceil(lastEvent.tMs / 1000) * 1000;

  const tracks: AarAnomalyTrack[] = resp.anomalies.map((anomaly) => ({
    anomaly,
    keyframes: buildAnomalyKeyframes(anomaly),
  }));

  const periods: AarPeriodTick[] = resp.operational_periods.map((p) => ({
    ...p,
    startMs: ms(p.start_at),
    endMs: ms(p.end_at),
  }));

  // #485 — deactivation gaps on the scrub clock. `deactivation_gaps` is optional-tolerant for
  // older API responses; degenerate/invalid spans are dropped so a bad row can't break the band.
  const gaps: AarDeactivationGapMs[] = (resp.deactivation_gaps ?? [])
    .map((g) => ({ startMs: ms(g.deactivated_at), endMs: ms(g.reactivated_at) }))
    .filter((g) => Number.isFinite(g.startMs) && Number.isFinite(g.endMs) && g.endMs > g.startMs);

  return { startMs, endMs, closed: resp.window.closed, periods, events, tracks, gaps };
}
